import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import TopFields, { FieldsConfig } from "@/components/layouts/TopFields";
import { StatusBadge } from "@/components/ui/status-badge";
import UserAvatar from "@/components/controls/UserAvatar";
import { Bullet } from "@dreamhub/designsystem/icons";
import { useUserSelect } from "@/entities/users/useUserSelect";
import { useProducts } from "@/entities/settings/hooks";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { DuplicateContractFields } from "./types";

interface DuplicateTopFieldsProps {
  sourceContractId: string;
  fields: DuplicateContractFields;
  currency: string | null;
  dateRangeLabel: string | null;
  tcv: number;
  cmrr: number;
  onFieldChange: (patch: Partial<DuplicateContractFields>) => void;
  onProductChange: (productId: string) => void;
}

const DuplicateTopFields: React.FC<DuplicateTopFieldsProps> = ({
  sourceContractId,
  fields,
  currency,
  dateRangeLabel,
  tcv,
  cmrr,
  onFieldChange,
  onProductChange
}) => {
  const { t } = useTranslation();

  const {
    options: userOptions,
    entities: userEntities,
    isLoading: isLoadingUsers,
    searchTerm: userSearchTerm,
    setSearchTerm: setUserSearchTerm
  } = useUserSelect();

  const {
    options: companyOptions,
    onSearch: onCompanyOptionsSearch,
    isLoading: isLoadingCompanies,
    searchTerm: companySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: fields.companyId || "",
    alwaysEnabled: true
  });

  const products = useProducts().data?.products ?? [];

  // The duplicate is not persisted yet, so every editable field writes to the
  // local draft; dates and financials stay read-only because the backend
  // derives them from the revenue items on create.
  const fieldsConfig: FieldsConfig = {
    companyId: {
      type: "select",
      icon: "company",
      entity: EntityType.COMPANY,
      options: companyOptions,
      searchTerm: companySearchTerm,
      showSearch: true,
      onSearch: onCompanyOptionsSearch,
      isLoading: isLoadingCompanies,
      popoverClassName: "max-w-[30vw]",
      onSelect: (companyId: string) => onFieldChange({ companyId })
    },
    productId: {
      type: "select",
      icon: "product",
      options: products.map((product) => ({
        id: product.id,
        displayName: product.name
      })),
      onSelect: onProductChange
    },
    contractDates: {
      type: "nonEditable",
      icon: "calendar",
      customComponent: (
        <Text
          variant={dateRangeLabel ? "body" : "bodyTertiary"}
          className="px-2"
          data-test="duplicate-top-fields-dates"
        >
          {dateRangeLabel ?? t("contracts.duplicate.emptyField")}
        </Text>
      )
    },
    csmOwner: {
      type: "select",
      entity: EntityType.USER,
      options: userOptions,
      showEmptyIcon: true,
      showSearch: true,
      searchTerm: userSearchTerm,
      onSearch: setUserSearchTerm,
      isLoading: isLoadingUsers,
      renderOptionIcon: (value) => (
        <UserAvatar
          userData={userEntities.find((user) => user.id === (value as string))}
          size="S"
          className="mr-1"
        />
      ),
      onSelect: (csmOwner: string) => onFieldChange({ csmOwner })
    },
    totalContractValue: {
      type: "nonEditable",
      icon: "arrMrr",
      customComponent: (
        <Text variant="body" className="px-2" data-test="duplicate-top-fields-tcv">
          {`${t("contracts.tcv")} ${formatCurrency(tcv, currency)}`}
        </Text>
      )
    },
    cmrr: {
      type: "nonEditable",
      icon: "arrMrr",
      customComponent: (
        <Text
          variant="body"
          className="px-2"
          data-test="duplicate-top-fields-cmrr"
        >
          {`${t("contracts.cmrr")} ${formatCurrency(cmrr, currency)}`}
        </Text>
      )
    },
    contractStatus: {
      type: "nonEditable",
      customComponent: (
        <StatusBadge
          statusColor="var(--border-field-default)"
          containerClassName="w-fit"
          className="border-none text-base"
          icon={<Bullet />}
          data-test="duplicate-contract-status-badge"
        >
          {t("contractTerms.table.statusValues.draft")}
        </StatusBadge>
      )
    }
  };

  return (
    <TopFields
      entity={{
        id: sourceContractId,
        companyId: fields.companyId ?? undefined,
        productId: fields.productId ?? undefined,
        csmOwner: fields.csmOwner ?? undefined
      }}
      fieldsConfig={fieldsConfig}
      onUpdateField={() => {}}
      className="px-3 pb-3"
    />
  );
};

export default DuplicateTopFields;
